import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { NotificationService, NotificationData } from './services/notification.service';
import { Subject, takeUntil, filter, finalize, firstValueFrom } from 'rxjs';
import { ModalStateService } from './services/modal-state.service';
import { NotificationModalComponent } from './components/notification-modal/notification-modal.component';
import { WayPurchaseModalComponent } from './components/way-purchase-modal/way-purchase-modal.component';
import { WayPurchaseModalStateService } from './components/way-purchase-modal/services/way-purchase-modal-state.service';
import { MultiEligibleSelectionModalComponent } from './components/multi-eligible-selection-modal/multi-eligible-selection-modal.component';
import { MultiEligibleSelectionModalStateService } from './components/multi-eligible-selection-modal/services/multi-eligible-selection-modal-state.service';
import { ApiService } from './services/api.service';
import type { PrepaidPurchaseStatus } from './models/prepaid-wash-book.models';
import { shouldOfferCodeAfterQrFailure } from './shared/utils/prepaid-redemption-code.util';
import { PrepaidCodeDialogComponent } from './shared/components/prepaid-code-dialog/prepaid-code-dialog.component';
import { AlertModalComponent } from './shared/components/alert-modal/alert-modal.component';
import { compressImageForLpr } from './shared/utils/lpr-image-compression';
import { openPusherNotificationModal } from './shared/utils/notification-modal-open.util';
import {
  isCameraMonitorOnlyNotification,
  isManualLprCaptureNotification,
} from './shared/utils/notification-display.util';
import { RoleService } from './services/role.service';
import { AttendanceService } from './services/attendance.service';
import { AttendancePresenceService } from './services/attendance-presence.service';
import { decodeQrFromImageFile } from './shared/utils/decode-qr-from-image.util';
import {
  parsePrepaidRedemptionUrl,
  PrepaidRedemptionScanPayload,
} from './shared/utils/prepaid-redemption-url.util';
import { PrepaidWashBookService } from './services/prepaid-wash-book.service';
import {
  buildCreateOrderRequestFromGetLpImagePrepaidRedeem,
  isGetLpImagePrepaidRedeemResponse,
} from './shared/utils/get-lp-image-prepaid-redeem.util';
import { TunnelPwaService } from './services/tunnel-pwa.service';
import { DeploymentNotificationHandlerService } from './services/deployment-notification-handler.service';
import { DeploymentNotificationService } from './services/deployment-notification.service';
import { ReceiptPrinterService } from './services/receipt-printer.service';
import { ActiveSubscriptionGateService } from './services/active-subscription-gate.service';
import { getUserDataLaneCameraRoi, getUserDataLaneId } from './shared/utils/auth-headers.util';
import { LaneAttentionStackComponent } from './shared/components/lane-attention-stack/lane-attention-stack.component';
import { ImpersonationBannerComponent } from './admin/impersonation/components/impersonation-banner/impersonation-banner.component';
import { LaneAutoRedeemToastComponent } from './shared/components/lane-auto-redeem-toast/lane-auto-redeem-toast.component';
import { ToastContainerComponent } from './shared/components/toast/toast-container.component';
import { ToastTemplateDirective } from './shared/components/toast/toast-template.directive';
import { LaneAutoRedeemToastService } from './shared/components/lane-auto-redeem-toast/lane-auto-redeem-toast.service';
import { isLaneAutoRedeemSuccessNotification } from './shared/utils/lane-auto-redeem-toast.util';
import { isFeedbackPublicPath } from './feedback/feedback-branding.util';
import { LoggerService } from './services/logger.service';
import { NotificationUtils } from './notifications/notification.utils';
import { resolveLprNotificationTraceContext } from './shared/utils/lpr-notification-trace.util';
import {
  buildRehydratedLprNotification,
  extractLprEntryRowFromApiResponse,
  getLprEntryIdFromNotification,
  isManualCaptureLprEntryRow,
  readMostRecentStoredLprNotification,
  resolveRehydratedNotificationFromEntry,
  shouldRehydrateStoredLprNotification,
} from './shared/utils/lpr-notification-rehydrate.util';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    NotificationModalComponent,
    WayPurchaseModalComponent,
    MultiEligibleSelectionModalComponent,
    AlertModalComponent,
    PrepaidCodeDialogComponent,
    LaneAttentionStackComponent,
    ImpersonationBannerComponent,
    LaneAutoRedeemToastComponent,
    ToastContainerComponent,
    ToastTemplateDirective,
  ],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  title = 'web-carwash';
  openCard = 'Personal Details';
  activeTab = 'User Profile';

  // LPR upload in progress (header scan and legacy flows; avoids concurrent uploads)
  isLprUploadInProgress = false;
  private isLprCaptureDialogOpen = false;

  /** Header “scan” icon on Orders dispatches this; same behavior as FAB capture. */
  private readonly onHeaderLprCaptureEvent = (): void => {
    const win = this.getLprCaptureWindowState();
    if (this.isLprUploadInProgress || this.isLprCaptureDialogOpen || win?.__wayLprCaptureBusy)
      return;
    this.isLprCaptureDialogOpen = true;
    if (win) {
      win.__wayLprCaptureBusy = true;
    }
    setTimeout(() => this.capturePhoto(), 50);
  };

  // CWS-1908: "Type the code instead". When a scanned prepaid QR cannot be resolved, this
  // dialog opens on its own so the attendant can type the code from the customer's SMS or
  // email; the lookup then opens the same redemption flow the QR would have opened.
  isPrepaidCodeDialogOpen = false;
  prepaidCodeBusy = false;
  prepaidCodeError = '';
  prepaidCodeIntro = '';

  private openPrepaidCodeDialog(intro: string): void {
    this.prepaidCodeIntro = intro;
    this.prepaidCodeError = '';
    this.isPrepaidCodeDialogOpen = true;
  }

  closePrepaidCodeDialog(): void {
    if (this.prepaidCodeBusy) {
      return;
    }
    this.isPrepaidCodeDialogOpen = false;
    this.prepaidCodeError = '';
  }

  async onPrepaidCodeFind(code: string): Promise<void> {
    if (this.prepaidCodeBusy) {
      return;
    }
    this.prepaidCodeBusy = true;
    this.prepaidCodeError = '';
    try {
      await this.ensureOrdersRouteForScan();
      const status = await firstValueFrom(
        this.prepaidWashBookService.resolvePrepaidPurchaseStatusFromCode(code)
      );
      this.isPrepaidCodeDialogOpen = false;
      this.openPrepaidRedemptionForStatus(status, 'prepaid_code');
    } catch (error: unknown) {
      this.prepaidCodeError =
        (error as { error?: { message?: string }; message?: string })?.error?.message ||
        (error as { message?: string })?.message ||
        'No prepaid book found for this code.';
    } finally {
      this.prepaidCodeBusy = false;
    }
  }

  // LPR error modal (use shared alert modal)
  showLprErrorModal = false;
  lprErrorTitle = 'Error';
  lprErrorDescription = 'Something went wrong. Please try again.';
  lprErrorAdditionalDescription = '';

  // Track current route to hide modal on public routes (login, public-stream, etc.)
  isPublicStreamRoute = false;

  /** Tunnel kiosk (login + orders) — shop-staff modals and APIs must not run here. */
  isTunnelRoute = false;

  /** LPR / Pusher notification modal auto-popup only on the Orders module (`/orders`). */
  isOrdersRoute = false;

  /** Previous navigation URL was Orders (used to close LPR modal when leaving Orders only). */
  private wasOnOrdersRoute = false;
  private lprRehydrateAttempted = false;

  private static readonly LPR_NOTIFICATION_TYPES: readonly string[] = [
    'fleet-vehicle-found',
    'fleet-invoice-added',
    'invalid-license-plate',
    'order-success',
    'create-payment-request',
    'create-customer-request',
    'create-order-request',
    'order-not-found',
    'detailing-appointment-found',
    'order-completed',
    'vehicle-not-found',
    'active-subscription',
    'paused-subscription',
    'exit-active-subscription',
    'exit-detailing-appointment-found',
    'way-single-wash',
    'way-active-subscription',
    'way-purchase-selection',
    'way-purchase-unredeemable',
    'way-subscription-already-redeemed-today',
    'multi-eligible-selection',
  ] as const;

  constructor(
    private notificationService: NotificationService,
    private modalStateService: ModalStateService,
    private wayPurchaseModalState: WayPurchaseModalStateService,
    private multiEligibleModalState: MultiEligibleSelectionModalStateService,
    private router: Router,
    private apiService: ApiService,
    private messageService: NotificationService,
    private roleService: RoleService,
    private prepaidWashBookService: PrepaidWashBookService,
    private tunnelPwaService: TunnelPwaService,
    private deploymentNotificationHandler: DeploymentNotificationHandlerService,
    private deploymentNotificationService: DeploymentNotificationService,
    private receiptPrinterService: ReceiptPrinterService,
    private activeSubscriptionGateService: ActiveSubscriptionGateService,
    private laneAutoRedeemToastService: LaneAutoRedeemToastService,
    private logger: LoggerService,
    private attendanceService: AttendanceService,
    private attendancePresenceService: AttendancePresenceService
  ) {}

  ngOnInit(): void {
    // Initialize notification system
    this.initializeNotificationSystem();
    this.scheduleLprNotificationRehydrate();
    this.initializeStaffAttendancePresence();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.checkCurrentRoute();
        this.tunnelPwaService.syncForUrl(this.router.url);
        if (this.isPublicStreamRoute || this.isTunnelRoute) {
          this.modalStateService.hideModal();
          this.wayPurchaseModalState.hideModal();
        } else if (
          !this.roleService.isShopFloorEmployee() &&
          this.wasOnOrdersRoute &&
          !this.isOrdersRoute
        ) {
          // Admin: auto-opened LPR notifications on Orders; close when leaving that module.
          this.modalStateService.hideModal();
          this.wayPurchaseModalState.hideModal();
        }
        this.wasOnOrdersRoute = this.isOrdersRoute;
        if (this.isOrdersRoute) {
          this.tryRehydrateRecentLprNotification();
        }
      });

    // Check initial route
    this.checkCurrentRoute();
    this.tunnelPwaService.syncForUrl(this.router.url);
    this.wasOnOrdersRoute = this.isOrdersRoute;

    if (typeof window !== 'undefined') {
      window.addEventListener('way-header-lpr-capture', this.onHeaderLprCaptureEvent);
    }

    // Probe the network receipt printer once on load so "Print Receipt" is instant. When it is
    // not present, receipts transparently fall back to the existing PDF print flow.
    void this.receiptPrinterService.refreshAvailability();
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('way-header-lpr-capture', this.onHeaderLprCaptureEvent);
    }

    this.destroy$.next();
    this.destroy$.complete();

    // Clean up any lingering file inputs
    this.cleanupAllFileInputs();
  }

  private getLprCaptureWindowState(): (Window & { __wayLprCaptureBusy?: boolean }) | null {
    return typeof window !== 'undefined'
      ? (window as Window & { __wayLprCaptureBusy?: boolean })
      : null;
  }

  private clearLprCaptureDialogState(): void {
    this.isLprCaptureDialogOpen = false;
    const win = this.getLprCaptureWindowState();
    if (win) {
      win.__wayLprCaptureBusy = false;
    }
  }

  private trackNotificationPresentation(
    event: string,
    notification: NotificationData,
    reason?: string
  ): void {
    this.logger.trackNotification(
      event,
      resolveLprNotificationTraceContext(notification, reason ? { reason } : undefined)
    );
  }

  /**
   * Staff idle presence: while checked in, report online/offline so the admin
   * employee list can show Offline after 5 minutes of browser inactivity.
   */
  private initializeStaffAttendancePresence(): void {
    if (!this.roleService.isStaff()) {
      return;
    }
    this.attendancePresenceService.init();
    this.attendanceService
      .getCurrentAttendanceStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: err => {
          this.logger.warn('Unable to bootstrap staff attendance presence', err, 'AppComponent');
        },
      });
  }

  private initializeNotificationSystem(): void {
    // Listen for notifications and show modal (or auto-redeem success toast).
    this.notificationService.notification$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notification => {
        if (!notification) {
          return;
        }
        // Monitored camera: the read still reaches the list and badge, and staff can open
        // it from there, but it must not present itself as a popup or toast.
        if (isCameraMonitorOnlyNotification(notification)) {
          this.trackNotificationPresentation('suppressed', notification, 'camera_monitor_only');
          return;
        }
        // Attendance-only staff: no LPR/notification popups or toasts.
        if (this.roleService.isStaff()) {
          this.trackNotificationPresentation('suppressed', notification, 'staff_role');
          return;
        }
        if (this.isPublicStreamRoute || this.isTunnelRoute) {
          this.trackNotificationPresentation(
            'suppressed',
            notification,
            this.isTunnelRoute ? 'tunnel_route' : 'public_route'
          );
          return;
        }
        // Attendant: show popup on any page.
        // Admin: show popup only on the Orders page — except mandatory multi-eligible
        // selection and header/UI image scans (user already initiated the flow).
        const isAttendant = this.roleService.isShopFloorEmployee();
        const allowOffOrdersPopup =
          notification.type === 'multi-eligible-selection' ||
          isManualLprCaptureNotification(notification);
        if (!isAttendant && !this.isOrdersRoute && !allowOffOrdersPopup) {
          this.trackNotificationPresentation('suppressed', notification, 'admin_not_on_orders');
          return;
        }
        // CWS-1053: toast and modal are mutually exclusive. The toast exists to tell the
        // attendant a wash was granted when nothing else will — which is only the Way
        // auto-redeem path. A vendor redeem opens the Active Subscription modal carrying the
        // same plate and package, so a toast beside it was pure duplication.
        if (isLaneAutoRedeemSuccessNotification(notification)) {
          this.trackNotificationPresentation('auto_redeem_toast', notification);
          this.laneAutoRedeemToastService.show(notification);
          return;
        }
        openPusherNotificationModal(
          this.modalStateService,
          this.wayPurchaseModalState,
          notification,
          this.activeSubscriptionGateService,
          this.logger,
          this.multiEligibleModalState
        );
      });
  }

  /**
   * Embedded Android WebViews can reload mid-SSE. Re-open a very recent stored LPR notification
   * once lane/auth context is ready so reload matches live SSE behavior.
   */
  private scheduleLprNotificationRehydrate(): void {
    if (typeof window === 'undefined') {
      return;
    }
    // Embedded Android WebViews can reload before auth/route context is ready.
    window.setTimeout(() => this.tryRehydrateRecentLprNotification(), 1500);
    window.setTimeout(() => this.tryRehydrateRecentLprNotification(), 4000);
  }

  private tryRehydrateRecentLprNotification(): void {
    if (this.lprRehydrateAttempted || this.isPublicStreamRoute || this.isTunnelRoute) {
      return;
    }

    // Attendance-only staff never re-open LPR/notification modals.
    if (this.roleService.isStaff()) {
      this.lprRehydrateAttempted = true;
      return;
    }

    const isAttendant = this.roleService.isShopFloorEmployee();
    if (!isAttendant && !this.isOrdersRoute) {
      return;
    }
    if (
      this.modalStateService.getCurrentState().isOpen ||
      this.wayPurchaseModalState.getCurrentState().isOpen ||
      this.multiEligibleModalState.getCurrentState().isOpen
    ) {
      return;
    }

    const stored = readMostRecentStoredLprNotification();
    if (!stored) {
      return;
    }
    if (!shouldRehydrateStoredLprNotification(stored)) {
      this.lprRehydrateAttempted = true;
      return;
    }

    this.lprRehydrateAttempted = true;

    const notification = buildRehydratedLprNotification(stored);
    // CWS-612: the stored copy is a snapshot. Check the lane entry's current state first so a
    // prompt that has since been paid does not come back as "Order Success!" after a reload.
    const lprEntryId = getLprEntryIdFromNotification(notification);
    if (!lprEntryId) {
      this.openRehydratedLprNotification(notification);
      return;
    }
    this.apiService
      .get(`order/lpr-entries/${lprEntryId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: unknown) => {
          const entryRow = extractLprEntryRowFromApiResponse(response);
          const current = resolveRehydratedNotificationFromEntry(notification, entryRow);
          if (!current) {
            // CWS-863: the row itself records a staff image scan, whatever copy history kept.
            this.logger.trackNotification(
              isManualCaptureLprEntryRow(entryRow)
                ? 'rehydrate_skipped_manual_capture'
                : 'rehydrate_skipped_finished',
              resolveLprNotificationTraceContext(notification)
            );
            return;
          }
          this.openRehydratedLprNotification(current);
        },
        // The entry could not be read: keep the pre-CWS-612 behaviour rather than lose a prompt.
        error: () => this.openRehydratedLprNotification(notification),
      });
  }

  private openRehydratedLprNotification(notification: NotificationData): void {
    // CWS-1053: same mutual exclusion as the live path above — toast only where no modal
    // follows, so a rehydrated vendor redeem does not show both.
    if (isLaneAutoRedeemSuccessNotification(notification)) {
      this.laneAutoRedeemToastService.show(notification);
      return;
    }

    this.logger.trackNotification(
      'rehydrated_stored_lpr',
      resolveLprNotificationTraceContext(notification)
    );
    openPusherNotificationModal(
      this.modalStateService,
      this.wayPurchaseModalState,
      notification,
      this.activeSubscriptionGateService,
      this.logger,
      this.multiEligibleModalState
    );
  }

  toggleCard(cardTitle: string) {
    this.openCard = this.openCard === cardTitle ? '' : cardTitle;
  }

  onTabChange(tabName: string) {
    this.activeTab = tabName;
  }

  onDeploymentReload(): void {
    this.deploymentNotificationService.reloadApplication();
  }

  private checkCurrentRoute(): void {
    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    // Hide modal on public routes: login, forgot-password, reset-password, resend-email, public-stream, etc.
    const publicRoutes = [
      '/bootstrap',
      '/login',
      '/forgot-password',
      '/reset-password',
      '/resend-email',
      '/public-stream',
      '/landing-page',
      '/sms-campaign-success-page',
      '/admin/login',
      '/receipt',
      '/prepaid/check-in',
    ];
    this.isPublicStreamRoute =
      publicRoutes.some(route => currentUrl.startsWith(route)) || isFeedbackPublicPath(currentUrl);
    this.isOrdersRoute = this.isOnOrdersModuleRoute(currentUrl);
    this.isTunnelRoute = this.isOnTunnelRoute(currentUrl);
  }

  /** Orders module route used by admin and staff (`app.routes` path: `orders`). */
  private isOnOrdersModuleRoute(url: string): boolean {
    return url === '/orders' || url.startsWith('/orders/');
  }

  /** Tunnel kiosk routes (login + orders) — hide shop-staff global UI. */
  private isOnTunnelRoute(url: string): boolean {
    return url === '/tunnel' || url.startsWith('/tunnel/');
  }

  onLprErrorModalClose(): void {
    this.showLprErrorModal = false;
    this.lprErrorAdditionalDescription = '';
  }

  private openLprErrorModal(description: string, title = 'Error'): void {
    this.lprErrorTitle = title;
    this.lprErrorDescription = description;
    const isRecognitionError = /recogni[sz]e|recogni[sz]ation|cannot recognize plate/i.test(
      description
    );
    this.lprErrorAdditionalDescription = isRecognitionError
      ? 'For best results: use a clear, front-on photo with good lighting, and ensure the plate fills most of the frame without blur or glare.'
      : '';
    this.showLprErrorModal = true;
  }

  private getNotificationTypeFromGetLpImage(apiData: any): NotificationData['type'] | null {
    if (isGetLpImagePrepaidRedeemResponse(apiData)) {
      return 'create-order-request';
    }

    // Nested recognitionResult / message shapes from get-lp-image (and full HTTP body).
    const candidates = [
      apiData?.data?.failure_type,
      apiData?.data?.data?.failure_type,
      apiData?.data?.data?.data?.failure_type,
      apiData?.status,
      apiData?.data?.status,
      apiData?.data?.data?.status,
      apiData?.data?.data?.data?.status,
      apiData?.event,
      apiData?.data?.event,
      apiData?.data?.data?.event,
      apiData?.redeem_event,
      apiData?.data?.redeem_event,
      apiData?.message,
      apiData?.data?.message,
      apiData?.data?.data?.message,
    ];

    for (const c of candidates) {
      if (typeof c !== 'string') continue;
      if (c === 'recent-lpr-entry-found') continue;
      if (AppComponent.LPR_NOTIFICATION_TYPES.includes(c)) {
        return c as NotificationData['type'];
      }
    }

    // Fallback: payload already has multi-eligible cards (status field may be numeric 200).
    const optionRoots = [
      apiData?.purchase_options,
      apiData?.data?.purchase_options,
      apiData?.data?.data?.purchase_options,
      apiData?.data?.data?.data?.purchase_options,
    ];
    for (const options of optionRoots) {
      if (Array.isArray(options) && options.length > 1) {
        return 'multi-eligible-selection';
      }
    }
    return null;
  }

  private getNotificationPayloadFromGetLpImage(apiData: any): any {
    // Prefer the innermost object that carries purchase_options / plate fields.
    const candidates = [apiData?.data?.data?.data, apiData?.data?.data, apiData?.data, apiData];
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') continue;
      if (
        Array.isArray(candidate.purchase_options) ||
        candidate.plate != null ||
        candidate.vehicle_license_plate != null ||
        candidate.lpr_entry_id != null
      ) {
        return candidate;
      }
    }
    return apiData?.data?.data ?? apiData?.data ?? apiData;
  }

  private enrichGetLpImagePayloadForLprSource(payload: any, apiData: any): any {
    const outer = apiData?.data ?? {};
    const nested = outer?.data ?? {};
    const lprEntry = payload?.lpr_entry ?? outer?.lpr_entry ?? nested?.lpr_entry ?? {};
    const laneId =
      payload?.lane_id ||
      outer?.lane_id ||
      nested?.lane_id ||
      lprEntry?.lane_id ||
      getUserDataLaneId() ||
      '';
    const cameraRoi =
      payload?.camera_roi ??
      outer?.camera_roi ??
      nested?.camera_roi ??
      lprEntry?.camera_roi ??
      getUserDataLaneCameraRoi();

    const pickImageUrl = (...candidates: any[]): string | undefined => {
      for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) {
          return c.trim();
        }
      }
      return undefined;
    };

    const imageS3Url = pickImageUrl(
      payload?.image_s3_url,
      payload?.imageS3Url,
      lprEntry?.image_s3_url,
      outer?.image_s3_url,
      nested?.image_s3_url
    );
    // UI image scan is plate-only: map any capture URL into the plate slot, never vehicle.
    const plateImageS3Url = pickImageUrl(
      payload?.plate_image_s3_url,
      payload?.plateImageS3Url,
      lprEntry?.plate_image_s3_url,
      outer?.plate_image_s3_url,
      nested?.plate_image_s3_url,
      imageS3Url
    );
    const lprImageUrl = pickImageUrl(
      payload?.lpr_image_url,
      payload?.lprImageUrl,
      outer?.lpr_image_url,
      nested?.lpr_image_url,
      plateImageS3Url
    );

    const imageSourceHint = [payload?.image_source, payload?.imageSource, outer?.image_source]
      .map(v => (typeof v === 'string' ? v.trim() : ''))
      .find(Boolean);

    const enriched: Record<string, any> = {
      ...payload,
      is_lpr_upload: true,
      source_context: 'lpr',
      // So the modal keeps the preview slot pending until images-ready (CWS-796).
      image_source: imageSourceHint || 'webflow',
      lpr_entry_id:
        payload?.lpr_entry_id ||
        payload?.lprEntryId ||
        lprEntry?.id ||
        lprEntry?.lpr_entry_id ||
        outer?.lpr_entry_id ||
        nested?.lpr_entry_id ||
        payload?.id ||
        '',
      licence_plate_id:
        payload?.licence_plate_id ||
        outer?.licence_plate_id ||
        nested?.licence_plate_id ||
        payload?.license_plate_id ||
        '',
      license_plate_id:
        payload?.license_plate_id ||
        outer?.license_plate_id ||
        nested?.license_plate_id ||
        payload?.licence_plate_id ||
        '',
      ...(laneId ? { lane_id: laneId } : {}),
      ...(cameraRoi != null ? { camera_roi: cameraRoi } : {}),
    };

    // Never stamp empty image strings — modal hydration treats '' as an explicit clear (CWS-796).
    // UI upload flow is plate-only: do not set image_s3_url even if the API used that field name.
    for (const key of [
      'image_s3_url',
      'imageS3Url',
      'plate_image_s3_url',
      'plateImageS3Url',
      'lpr_image_url',
      'lprImageUrl',
    ] as const) {
      delete enriched[key];
    }
    if (plateImageS3Url) {
      enriched['plate_image_s3_url'] = plateImageS3Url;
    }
    if (lprImageUrl) {
      enriched['lpr_image_url'] = lprImageUrl;
    }

    return enriched;
  }

  private emitNotificationFromGetLpImage(apiData: any): void {
    // Callers may pass the full HTTP body `{ status: true, data: recognitionResult }` or
    // recognitionResult itself. Prefer the recognitionResult when present.
    const recognitionResult =
      apiData?.data != null &&
      typeof apiData.data === 'object' &&
      !Array.isArray(apiData.data) &&
      (typeof apiData.data.status === 'string' ||
        apiData.data.data != null ||
        Array.isArray(apiData.data.purchase_options))
        ? apiData.data
        : apiData;

    const type =
      this.getNotificationTypeFromGetLpImage(recognitionResult) ||
      this.getNotificationTypeFromGetLpImage(apiData);
    if (!type) {
      this.logger.warn(
        'get-lp-image: no notification type resolved (multi-eligible may need images-ready fallback)',
        {
          status: recognitionResult?.status ?? apiData?.status,
          nestedStatus: recognitionResult?.data?.status,
          hasPurchaseOptions: Array.isArray(
            recognitionResult?.purchase_options ??
              recognitionResult?.data?.purchase_options ??
              apiData?.data?.purchase_options
          ),
        },
        'NotificationFlow'
      );
      return;
    }

    if (
      isGetLpImagePrepaidRedeemResponse(recognitionResult) ||
      isGetLpImagePrepaidRedeemResponse(apiData)
    ) {
      const prepaidRedeem = buildCreateOrderRequestFromGetLpImagePrepaidRedeem(recognitionResult);
      const payload = this.enrichGetLpImagePayloadForLprSource(
        prepaidRedeem.data,
        recognitionResult
      );
      this.notificationService.emitNotification(prepaidRedeem.type, payload, prepaidRedeem.message);
      return;
    }

    const payload = this.enrichGetLpImagePayloadForLprSource(
      this.getNotificationPayloadFromGetLpImage(recognitionResult),
      recognitionResult
    );
    // Prefer human copy. Older backends / payloads echoed the event slug as `message`
    // (SSE already maps Way events to staff-facing text).
    const candidates = [
      payload?.message,
      recognitionResult?.message,
      recognitionResult?.data?.message,
      apiData?.data?.message,
      apiData?.message,
    ]
      .map(value => String(value ?? '').trim())
      .filter(Boolean);
    const canonical = NotificationUtils.getMessageForEventType(type);
    const human = candidates.find(value => value !== type) ?? canonical;

    // CWS-889: open Choose Membership/Package immediately from the HTTP response.
    // Do not rely only on SSE (lane filter / admin Orders gating can drop the event).
    if (type === 'multi-eligible-selection') {
      const notification: NotificationData = {
        type,
        message: human,
        timestamp: new Date(),
        data: payload,
      };
      this.logger.trackNotification(
        'get_lp_image_multi_eligible_direct_open',
        resolveLprNotificationTraceContext(notification)
      );
      this.multiEligibleModalState.openModal({
        notification,
        behavior: 'replace',
        shouldStore: true,
      });
      // Still emit so notification list / badge stay in sync.
      this.notificationService.emitNotification(type, payload, human);
      return;
    }

    this.notificationService.emitNotification(type, payload, human);
  }

  /** Upload LPR image (already compressed when from camera). Handles response and photo-captured event. */
  private uploadLprImage(
    fileToUpload: File,
    originalFile: File,
    timing?: {
      captureAtMs: number;
      capturePerf: number;
      conversionStartMs: number;
      conversionStartPerf: number;
      conversionEndMs: number;
      conversionEndPerf: number;
    }
  ): void {
    const apiStartMs = Date.now();
    const apiStartPerf = performance.now();
    if (timing) {
      console.log(`[LPR Timing] API call started at: ${new Date(apiStartMs).toISOString()}`);
    }

    this.apiService
      .uploadLicensePlateImage(fileToUpload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          /* success path flips busy off after confirm */
        })
      )
      .subscribe({
        next: (response: any) => {
          this.messageService.clear();
          const responseMs = Date.now();
          const responsePerf = performance.now();
          if (timing) {
            this.logLprTiming(timing, apiStartMs, apiStartPerf, responseMs, responsePerf);
          }

          if (response.status) {
            const lprDataMessage = response.data?.message;
            if (lprDataMessage === 'recent-lpr-entry-found') {
              this.messageService.add({
                severity: 'info',
                summary: 'Same vehicle',
                detail:
                  'A recent visit was found for this plate. You can create another order if needed.',
                life: 6000,
              });
            }

            if (
              response.data?.status === 400 &&
              response.data?.data?.failure_type === 'create-customer-request'
            ) {
              const notificationData = response.data.data;
              const lprPayload = this.enrichGetLpImagePayloadForLprSource(
                notificationData,
                response.data
              );
              const notification: NotificationData = {
                type: 'create-customer-request',
                message:
                  lprPayload.message || 'Customer not found. Please create customer and order.',
                timestamp: new Date(),
                data: {
                  ...lprPayload,
                  plate: lprPayload.plate || lprPayload.vehicle_license_plate || '',
                  vehicle_license_plate: lprPayload.vehicle_license_plate || lprPayload.plate || '',
                  state: lprPayload.state || lprPayload.vehicle_state || '',
                  vehicle_state: lprPayload.vehicle_state || lprPayload.state || '',
                  location_id: lprPayload.location_id || '',
                  branch: lprPayload.branch || '',
                  vehicle_make: lprPayload.vehicle_make || '',
                  vehicle_model: lprPayload.vehicle_model || '',
                  vehicle_year: lprPayload.vehicle_year ?? '',
                  vehicle_vin: lprPayload.vehicle_vin || '',
                  userId: lprPayload.userId || lprPayload.user_id || '',
                  user_id: lprPayload.user_id || lprPayload.userId || '',
                  failure_type: lprPayload.failure_type || 'create-customer-request',
                  failure_reason: lprPayload.failure_reason || 'customer not found',
                },
              };
              this.notificationService.emitNotification(
                'create-customer-request',
                notification.data,
                notification.message
              );
              this.isLprUploadInProgress = false;
              this.clearLprCaptureDialogState();
            } else {
              this.emitNotificationFromGetLpImage(response);
              this.isLprUploadInProgress = false;
              this.clearLprCaptureDialogState();
              const reader = new FileReader();
              reader.onload = (e: any) => {
                window.dispatchEvent(
                  new CustomEvent('photo-captured', {
                    detail: {
                      file: fileToUpload,
                      dataUrl: e.target.result,
                      fileName: fileToUpload.name,
                      fileSize: fileToUpload.size,
                      fileType: fileToUpload.type,
                      apiResponse: response,
                    },
                  })
                );
              };
              reader.onerror = () => console.error('Error reading file');
              reader.readAsDataURL(fileToUpload);
            }
          } else {
            if (response.message === 'Cannot recognize plate' || response.status_code === 400) {
              this.messageService.add({
                severity: 'error',
                summary: 'LPR Error',
                detail: response.message || 'Cannot recognize plate',
                life: 6000,
              });
            } else {
              this.openLprErrorModal(response.message || 'Failed to process license plate image');
            }
            this.isLprUploadInProgress = false;
            this.clearLprCaptureDialogState();
          }
        },
        error: (error: any) => {
          this.messageService.clear();
          const responseMs = Date.now();
          const responsePerf = performance.now();
          if (timing) {
            this.logLprTiming(timing, apiStartMs, apiStartPerf, responseMs, responsePerf, true);
          }

          console.error('Error uploading license plate image:', error);
          const apiError = error?.error;
          if (apiError?.message === 'Cannot recognize plate' || error?.status === 400) {
            this.messageService.add({
              severity: 'error',
              summary: 'LPR Error',
              detail: apiError?.message || 'Cannot recognize plate',
              life: 6000,
            });
          } else {
            this.openLprErrorModal(
              apiError?.message || 'Failed to upload license plate image. Please try again.'
            );
          }
          this.isLprUploadInProgress = false;
          this.clearLprCaptureDialogState();
        },
      });
  }

  private async ensureOrdersRouteForScan(): Promise<void> {
    if (this.isOrdersRoute) {
      return;
    }
    const ok = await this.router.navigate(['/orders']);
    if (ok) {
      await new Promise(resolve => setTimeout(resolve, 50));
      this.checkCurrentRoute();
    }
  }

  private async handlePrepaidQrRedemption(redemption: PrepaidRedemptionScanPayload): Promise<void> {
    this.messageService.clear();
    this.messageService.add({
      severity: 'info',
      summary: 'Processing QR code',
      detail: 'Loading prepaid wash membership. Please wait.',
      life: 5000,
    });

    try {
      await this.ensureOrdersRouteForScan();

      const status = await firstValueFrom(
        this.prepaidWashBookService.resolvePrepaidPurchaseStatusFromScan(redemption)
      );

      this.messageService.clear();
      this.openPrepaidRedemptionForStatus(status, 'prepaid_qr');
    } catch (error: any) {
      this.messageService.clear();
      const detail =
        (error as { error?: { message?: string }; message?: string })?.error?.message ||
        (error as { message?: string })?.message ||
        'Could not load prepaid membership from QR code.';
      // CWS-1908: the QR did not work, so offer the code instead of only reporting the
      // failure — but only when the QR itself was refused. An outage would fail the code
      // lookup the same way, so there it stays an ordinary error.
      if (shouldOfferCodeAfterQrFailure(error)) {
        this.openPrepaidCodeDialog(
          `${detail} Ask the customer for the redemption code from their SMS or email and type it below.`
        );
        return;
      }
      this.messageService.add({
        severity: 'error',
        summary: 'Could not check the QR code',
        detail,
        life: 6000,
      });
    } finally {
      this.clearLprCaptureDialogState();
    }
  }

  /**
   * Open the create-order modal in prepaid redemption mode for a book found by QR scan or by
   * its redemption code (CWS-1908). `qr_scan` is what the modal keys its bootstrap on: it
   * means "found by lookup, not by the camera", so a typed code sets it too.
   */
  private openPrepaidRedemptionForStatus(
    status: PrepaidPurchaseStatus,
    sourceContext: 'prepaid_qr' | 'prepaid_code'
  ): void {
    this.modalStateService.forceShowModal(
      {
        type: 'create-order-request',
        message: 'Prepaid wash redemption',
        timestamp: new Date(),
        data: {
          prepaid_purchase_id: status.prepaid_purchase_id,
          qr_scan: true,
          source_context: sourceContext,
          customer_id: status.customer_id,
          purchase_vehicle_id: status.purchase_vehicle_id,
        },
      },
      false
    );
  }

  private processSelectedScanImage(file: File, cleanupInput: () => void): void {
    void (async () => {
      const decoded = await decodeQrFromImageFile(file);
      const qrText = decoded.kind === 'decoded' ? decoded.text : null;
      const redemption = qrText ? parsePrepaidRedemptionUrl(qrText) : null;

      if (redemption) {
        cleanupInput();
        await this.handlePrepaidQrRedemption(redemption);
        return;
      }

      // CWS-1908: the photo held a QR, either one that is not a prepaid wash QR or one the
      // decoder found but could not read. Either way the attendant scanned a code, not a plate,
      // so offer the typed code rather than sending the photo to plate recognition.
      if (decoded.kind !== 'none') {
        cleanupInput();
        await this.ensureOrdersRouteForScan();
        this.openPrepaidCodeDialog(
          decoded.kind === 'unreadable'
            ? "The QR code couldn't be read. Ask the customer for the redemption code from their SMS or email and type it below."
            : "That isn't a prepaid wash QR code. Ask the customer for the redemption code from their SMS or email and type it below."
        );
        this.clearLprCaptureDialogState();
        return;
      }

      // Admin LPR popups are Orders-scoped; navigate first so the result modal can open.
      await this.ensureOrdersRouteForScan();

      this.isLprUploadInProgress = true;

      const captureAtMs = Date.now();
      const capturePerf = performance.now();
      console.log(`[LPR Timing] Image reached app at: ${new Date(captureAtMs).toISOString()}`);

      this.messageService.clear();
      this.messageService.add({
        severity: 'info',
        summary: 'Processing license plate',
        detail:
          'License plate is being processed. Please wait. Use a clear, front-on photo for best results.',
        life: 5000,
      });

      const conversionStartMs = Date.now();
      const conversionStartPerf = performance.now();
      console.log(
        `[LPR Timing] Conversion started at: ${new Date(conversionStartMs).toISOString()}`
      );

      compressImageForLpr(file)
        .then(fileToUpload => {
          const conversionEndMs = Date.now();
          const conversionEndPerf = performance.now();
          console.log(
            `[LPR Timing] Conversion ended at: ${new Date(conversionEndMs).toISOString()}`
          );

          const timing = {
            captureAtMs,
            capturePerf,
            conversionStartMs,
            conversionStartPerf,
            conversionEndMs,
            conversionEndPerf,
          };

          return this.getImageDimensions(fileToUpload)
            .then(({ width, height }) => {
              const sizeKb = fileToUpload.size / 1024;
              const sizeText =
                sizeKb < 1024 ? `${sizeKb.toFixed(1)} KB` : `${(sizeKb / 1024).toFixed(2)} MB`;
              console.log(`LPR upload image size: ${sizeText}, resolution: ${width}x${height}`);
            })
            .catch(() => undefined)
            .finally(() => {
              this.uploadLprImage(fileToUpload, file, timing);
            });
        })
        .catch(() => {
          const conversionEndMs = Date.now();
          const conversionEndPerf = performance.now();
          console.log(
            `[LPR Timing] Conversion ended at: ${new Date(conversionEndMs).toISOString()}`
          );

          const timing = {
            captureAtMs,
            capturePerf,
            conversionStartMs,
            conversionStartPerf,
            conversionEndMs,
            conversionEndPerf,
          };

          this.getImageDimensions(file)
            .then(({ width, height }) => {
              const sizeKb = file.size / 1024;
              const sizeText =
                sizeKb < 1024 ? `${sizeKb.toFixed(1)} KB` : `${(sizeKb / 1024).toFixed(2)} MB`;
              console.log(`LPR upload image size: ${sizeText}, resolution: ${width}x${height}`);
            })
            .catch(() => undefined)
            .finally(() => {
              this.uploadLprImage(file, file, timing);
            });
        })
        .finally(() => {
          cleanupInput();
        });
    })();
  }

  private capturePhoto(): void {
    if (this.isLprUploadInProgress) {
      this.clearLprCaptureDialogState();
      return;
    }

    // Close any existing modal to ensure clean state for new upload
    this.modalStateService.hideModal();

    // Clean up any lingering file inputs from previous uploads
    this.cleanupAllFileInputs();

    // Create a hidden file input element with unique ID
    const input = document.createElement('input');
    const inputId = `lpr-file-input-${Date.now()}`;
    input.id = inputId;
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use back camera on mobile devices
    input.style.display = 'none'; // Hide the input element
    input.style.position = 'absolute'; // Ensure it doesn't affect layout
    input.style.left = '-9999px'; // Move off-screen
    let selectionHandled = false;

    // Helper function to clean up input element
    const cleanupInput = () => {
      try {
        if (input && input.parentNode) {
          input.parentNode.removeChild(input);
        }
      } catch (error) {
        console.warn('Error removing input element:', error);
      }
    };

    input.onchange = (event: any) => {
      if (selectionHandled) {
        return;
      }
      selectionHandled = true;
      // CRITICAL: Prevent default behavior that might cause page refresh
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }

      const file = event.target?.files?.[0];
      if (file) {
        this.processSelectedScanImage(file, cleanupInput);
      } else {
        // No file selected, clean up immediately
        this.clearLprCaptureDialogState();
        cleanupInput();
      }
    };

    input.onerror = (event: any) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      console.error('Error accessing camera');
      this.openLprErrorModal('Failed to access camera. Please try again.');
      this.clearLprCaptureDialogState();
      cleanupInput();
    };

    input.oncancel = (event: any) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.clearLprCaptureDialogState();
      cleanupInput();
    };

    // Append to body temporarily (required for some mobile browsers)
    document.body.appendChild(input);

    // Trigger the file input click with a small delay to ensure DOM is ready
    setTimeout(() => {
      if (input && input.parentNode) {
        input.click();
      }
    }, 10);
  }

  /**
   * Clean up all lingering file input elements (prevents memory leaks and conflicts)
   */
  private cleanupAllFileInputs(): void {
    try {
      // Remove any file inputs that might have been left behind
      const oldInputs = document.querySelectorAll('input[type="file"][id^="lpr-file-input-"]');
      oldInputs.forEach(input => {
        try {
          if (input.parentNode) {
            input.parentNode.removeChild(input);
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      });
    } catch (error) {
      console.warn('Error cleaning up old file inputs:', error);
    }
  }

  private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      try {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          const width = img.naturalWidth;
          const height = img.naturalHeight;
          URL.revokeObjectURL(url);
          if (width && height) {
            resolve({ width, height });
          } else {
            reject(new Error('Invalid image dimensions'));
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load image'));
        };
        img.src = url;
      } catch (error) {
        reject(error);
      }
    });
  }

  private logLprTiming(
    timing: {
      captureAtMs: number;
      capturePerf: number;
      conversionStartMs: number;
      conversionStartPerf: number;
      conversionEndMs: number;
      conversionEndPerf: number;
    },
    apiStartMs: number,
    apiStartPerf: number,
    responseMs: number,
    responsePerf: number,
    isError = false
  ): void {
    const captureToConversionStart = timing.conversionStartPerf - timing.capturePerf;
    const conversionDuration = timing.conversionEndPerf - timing.conversionStartPerf;
    const conversionToApiStart = apiStartPerf - timing.conversionEndPerf;
    const apiDuration = responsePerf - apiStartPerf;
    const totalDuration = responsePerf - timing.capturePerf;

    const statusLabel = isError ? 'ERROR' : 'SUCCESS';

    console.log(
      `[LPR Timing] API response (${statusLabel}) at: ${new Date(responseMs).toISOString()}`
    );
    console.log(
      `[LPR Timing] Capture → Conversion start: ${captureToConversionStart.toFixed(1)} ms`
    );
    console.log(`[LPR Timing] Conversion duration: ${conversionDuration.toFixed(1)} ms`);
    console.log(`[LPR Timing] Conversion end → API start: ${conversionToApiStart.toFixed(1)} ms`);
    console.log(`[LPR Timing] API duration: ${apiDuration.toFixed(1)} ms`);
    console.log(`[LPR Timing] Total (Capture → Response): ${totalDuration.toFixed(1)} ms`);
  }
}
